import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@1trading/react';
import { useState } from 'react';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { extractFileName } from '@/utils/string';

interface ProcessCleanupDialogProps {
  reportKey: string;
  accountHolderIds: string[];
  /** Starts the run with the collected justification; a throw keeps the confirm dialog open with the error */
  onConfirm: (justification: string) => Promise<void>;
  /** `didRequest` true once the API accepted the run */
  onClose: (didRequest: boolean) => void;
}

export const ProcessCleanupDialog = ({ reportKey, accountHolderIds, onConfirm, onClose }: ProcessCleanupDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose(hasRequested);
  };

  const handleConfirm = () =>
    confirmAction({
      title: 'Confirm account cleanup',
      description: `Permanently deletes all data for ${accountHolderIds.length} account(s): exchange auth records, FAST records, PII vault files, the Auth0 user and the OneSignal user, and deactivates the Sumsub applicant. Each account is re-checked first and skipped if it no longer qualifies. This cannot be undone. Your action here will be logged and will be auditable.`,
      actionLabel: 'Delete accounts',
      destructive: true,
      onConfirm: async (justification) => {
        setIsSubmitting(true);
        try {
          await onConfirm(justification);
          setHasRequested(true);
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : handleClose())} open>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Delete the following accounts?</DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          <p className="text-sm">
            From report <span className="font-mono text-xs">{extractFileName(reportKey)}</span>. The run takes a few minutes; the outcome CSV is announced as a toast when ready and
            appears in the report list.
          </p>
          <div className="flex flex-col gap-1 font-mono text-xs">
            {accountHolderIds.map((accountHolderId) => (
              <span key={accountHolderId}>{accountHolderId}</span>
            ))}
          </div>
          {hasRequested ? <p className="text-sm text-muted-foreground">Run requested. You can close this dialog.</p> : null}
        </div>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={handleClose} type="button" variant={hasRequested ? 'primary' : 'destructive'}>
            Close
          </Button>
          {hasRequested ? null : (
            <Button disabled={isSubmitting} onClick={handleConfirm} type="button" variant="destructive">
              Delete accounts
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
ProcessCleanupDialog.displayName = 'ProcessCleanupDialog';
